import { DEFAULT_LANGUAGE, isAppLanguage, type AppLanguage } from '@tk/shared';
import { useSyncExternalStore } from 'react';

/**
 * Käyttöliittymän kieli.
 *
 * Valinta on käyttäjäkohtainen mutta selainkohtainen: se elää vain
 * localStoragessa eikä kulje palvelimen kautta. Sama ratkaisu kuin
 * teemalla (`teema.ts`), ja samasta syystä — kieli on lukemisen apuväline,
 * ei käyttäjän ominaisuus, eikä sen vaihtaminen saa vaatia palvelinkutsua
 * eikä kirjautumista uudelleen.
 *
 * Ilman tallennettua valintaa seurataan selaimen kieltä. Suomenkielisellä
 * selaimella sovellus näyttää siis täsmälleen samalta kuin ennen tätä
 * ominaisuutta, ja englanninkielisen selaimen käyttäjä saa englannin
 * löytämättä asetusta ensin.
 *
 * Avain ja arvot on toistettu index.html:n rivikäsikirjoituksessa, joka
 * asettaa `<html lang>`-määreen ennen ensimmäistä maalausta. Jos muutat
 * niitä, muuta molemmat.
 */
export type Kieli = AppLanguage;

const AVAIN = 'tk.kieli';

/** Selain voi kieltää localStoragen (yksityinen selaus, evästeasetukset). */
function lueValinta(): Kieli {
  try {
    const arvo = localStorage.getItem(AVAIN);
    if (isAppLanguage(arvo)) return arvo;
  } catch {
    // Ei tallennustilaa: pudotaan selaimen kieleen.
  }
  return selaimenKieli();
}

/**
 * `navigator.languages` on toivejärjestyksessä. Ensimmäinen tuettu
 * voittaa; jos mikään ei osu, sovelluksen oma kieli on suomi.
 */
function selaimenKieli(): Kieli {
  const toiveet = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const toive of toiveet) {
    const juuri = toive.toLowerCase().split('-')[0];
    if (isAppLanguage(juuri)) return juuri;
  }
  return DEFAULT_LANGUAGE;
}

function kirjoitaDomiin(kieli: Kieli): void {
  document.documentElement.lang = kieli;
}

let nykyinen: Kieli = lueValinta();
const kuuntelijat = new Set<() => void>();

// index.html asettaa saman arvon ennen ensimmäistä maalausta. Tämä pitää
// määreen oikeana myös silloin, jos rivikäsikirjoitus joskus katoaa.
kirjoitaDomiin(nykyinen);

function ilmoita(): void {
  for (const kuuntelija of kuuntelijat) kuuntelija();
}

export function asetaKieli(kieli: Kieli): void {
  nykyinen = kieli;
  try {
    localStorage.setItem(AVAIN, kieli);
  } catch {
    // Valinta jää voimaan vain tälle istunnolle. Parempi kuin kaatuminen.
  }
  kirjoitaDomiin(kieli);
  ilmoita();
}

function tilaa(kuuntelija: () => void): () => void {
  kuuntelijat.add(kuuntelija);
  return () => {
    kuuntelijat.delete(kuuntelija);
  };
}

/** Voimassa oleva kieli. Kaikki tekstiä näyttävä koodi kulkee tämän kautta. */
export function useKieli(): Kieli {
  return useSyncExternalStore(tilaa, () => nykyinen);
}

// Sama työasema, monta välilehteä: valinta on selaimen asetus, joten sen
// kuuluu näkyä kaikissa auki olevissa välilehdissä eikä vain siinä, jossa
// se tehtiin.
window.addEventListener('storage', (tapahtuma) => {
  if (tapahtuma.key !== null && tapahtuma.key !== AVAIN) return;
  nykyinen = lueValinta();
  kirjoitaDomiin(nykyinen);
  ilmoita();
});

/**
 * Kieli ilman koukkua. Vain sellaisiin kohtiin, joissa React ei ole
 * mukana — muualla `useKaannos`, joka piirtää näkymän uudelleen kielen
 * vaihtuessa.
 */
export function kieliNyt(): Kieli {
  return nykyinen;
}
